/**
 * Case-home first eligible Safety Scan finding.
 * Assembles WHERE / WHEN / HOW OFTEN / usable hours / recurrence / operating finding
 * from persisted Scan / OM facts. Does not invent measurement or compute ungated rates.
 */

import { formatHumanScanWindow } from '../acceptance/scanVerifyNavigation';
import {
  customerScanCameraLabel,
  customerScanConditionLabel,
  customerScanZoneLabel,
} from '../acceptance/safetyScanProductPresentation';
import { resolveConfiguredCameraLabel, resolveConfiguredZoneLabel, replaceInternalLocationTokens } from '../customerLabels/customerFacingControlLabel';
import { CAMERA_LEVEL_SCOPE_LABEL, formatCameraLevelScope, isCameraLevelScope } from './cameraScopeDisplay';
import { presentStoredScanMeasurementBasis } from '../safetyScanReport/canonicalStoredMeasurementBasis';
import { readWorkplaceCondition } from '../workplaceCondition/workplaceCondition';

export const FIRST_SCAN_EMPTY = 'No Safety Scan has been created for this Case yet.';
export const FIRST_SCAN_BASELINE_WAITING =
  'Baseline waiting — the next clean measurement window has not produced a Safety Scan yet.';
export const MEASUREMENT_WINDOW_CAPTION = 'Measurement window';
export const NO_CLEAR_TIME_CONCENTRATION = 'Time concentration is not assessed for this Scan';
export const TIME_CONCENTRATION_UNASSESSED = NO_CLEAR_TIME_CONCENTRATION;
export const COVERAGE_UNAVAILABLE = 'Usable hours are not recorded for this Scan';
export const OBSERVED_UNAVAILABLE = 'Confirmed reviewed observations are not recorded for this Scan';
export const RECURRENCE_UNAVAILABLE = 'Recurrence evidence is not recorded for this Scan';
export const FINDING_UNAVAILABLE = 'An operating finding is not recorded for this Scan';
export const WHERE_UNAVAILABLE = 'Scan scope is not recorded';

const CAUSAL_RE = /\b(caused|because|due to|prevented|reduced risk|improved safety by)\b/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type FirstScanFindingView = {
  empty: boolean;
  emptyLabel: string | null;
  signed: boolean;
  reportId: string | null;
  windowLabel: string;
  whereLabel: string;
  whenLabel: string;
  concentrationLabel: string;
  observedLabel: string;
  coverageLabel: string;
  rateLabel: string | null;
  recurrenceLabel: string;
  findingLabel: string;
  openScanHref: string | null;
  historical: boolean;
  logicalScanCount: number;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function text(value: unknown): string {
  return String(value || '').trim();
}

function looksLikeUuid(value: string): boolean {
  return UUID_RE.test(value);
}

function readableLabel(value: unknown): string {
  const raw = text(value);
  if (!raw || looksLikeUuid(raw) || raw === '_camera_scope') return '';
  return raw;
}

function optionalNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function hasCausalLanguage(value: string): boolean {
  return CAUSAL_RE.test(value);
}

function nested(source: unknown, path: string[]): unknown {
  let current: unknown = source;
  for (const key of path) {
    const blob = asRecord(current);
    if (!(key in blob)) return undefined;
    current = blob[key];
  }
  return current;
}

function firstText(...values: unknown[]): string {
  for (const value of values) {
    const raw = readableLabel(value);
    if (raw) return raw;
  }
  return '';
}

function reportBodies(report: unknown): Record<string, unknown>[] {
  const root = asRecord(report);
  const inner = asRecord(root.report);
  return [root, inner, asRecord(inner.report_payload), asRecord(inner.report_content), asRecord(inner.finalized_payload), asRecord(root.report_payload), asRecord(root.report_content)];
}

function funnelFrom(report: unknown, progress?: Record<string, unknown> | null): Record<string, unknown> {
  const bodies = reportBodies(report);
  for (const body of bodies) {
    const funnel = asRecord(body.observation_funnel);
    if (Object.keys(funnel).length) return { ...funnel, ...asRecord(funnel.dispositions), ...asRecord(funnel.progress) };
    const dispositions = asRecord(body.dispositions);
    if (Object.keys(dispositions).length) return dispositions;
  }
  return { ...asRecord(progress) };
}

function measurementFrom(report: unknown): Record<string, unknown> {
  const bodies = reportBodies(report);
  for (const body of bodies) {
    const measurement = asRecord(body.measurement || body.exposure || body.coverage);
    if (Object.keys(measurement).length) return measurement;
    const customer = asRecord(body.customer_safety_scan || body.customer_scan);
    if (customer.total_usable_hours != null || customer.rates_available != null) return customer;
  }
  return {};
}

function timeConcentrationFrom(report: unknown): {
  peakWhen: string;
  buckets: unknown[];
  ratesAvailable: boolean;
} {
  const bodies = reportBodies(report);
  for (const body of bodies) {
    const customer = asRecord(body.customer_safety_scan || body.customer_scan || body);
    const peakWhen = firstText(
      customer.peak_when_label,
      nested(customer, ['peak_bucket', 'label']),
      nested(body, ['safety_finding', 'peak_period']),
      nested(body, ['executive_summary', 'safety_finding', 'peak_period']),
    );
    const buckets = Array.isArray(customer.time_concentration)
      ? customer.time_concentration
      : (Array.isArray(customer.time_buckets) ? customer.time_buckets : []);
    if (peakWhen || buckets.length) {
      return {
        peakWhen,
        buckets,
        ratesAvailable: customer.rates_available === true,
      };
    }
  }
  return { peakWhen: '', buckets: [], ratesAvailable: false };
}

function hotspotProof(report: unknown, concentration: { peakWhen: string; buckets: unknown[] }): boolean {
  if (concentration.peakWhen) return true;
  if (concentration.buckets.length >= 2) return true;
  const bodies = reportBodies(report);
  for (const body of bodies) {
    const visuals = Array.isArray(body.report_visuals) ? body.report_visuals : [];
    if (visuals.some((item) => /hotspot_concentration|artifact_hotspot/i.test(text(asRecord(item).id)))) {
      return true;
    }
  }
  return false;
}

function whereLabel(args: {
  report?: unknown;
  caseScope?: Record<string, unknown> | null;
  locationLabel?: string | null;
  cameraId?: string | null;
  concentrationProof: boolean;
}): string {
  const bodies = reportBodies(args.report);
  const scope = asRecord(args.caseScope);
  const anchorScope = asRecord(nested(bodies[1], ['workflow_anchor', 'scope']) || nested(bodies[0], ['workflow_anchor', 'scope']));
  const hotspot = args.concentrationProof
    ? firstText(
      ...bodies.map((body) => nested(body, ['safety_finding', 'primary_hotspot'])),
      ...bodies.map((body) => nested(body, ['executive_summary', 'safety_finding', 'primary_hotspot'])),
    )
    : '';
  if (hotspot && !isCameraLevelScope(hotspot)) {
    return resolveConfiguredZoneLabel(hotspot) || customerScanZoneLabel(hotspot) || hotspot;
  }
  const stored = presentStoredScanMeasurementBasis({
    persisted: asRecord(args.report),
    riskType: text(scope.risk_type || scope.riskType),
  });
  if (stored.source === 'stored' && stored.areaLabel) {
    return [stored.areaLabel, stored.cameraLabel, stored.scopeLabel].filter(Boolean).join(' · ');
  }
  const namedZone = firstText(
    args.locationLabel,
    customerScanZoneLabel(text(anchorScope.zone_id || anchorScope.zone_label)),
    resolveConfiguredZoneLabel(text(anchorScope.zone_label || anchorScope.zone_id || scope.zone_label || scope.zone_id)),
    customerScanZoneLabel(text(scope.zone_label || scope.zone_id || (Array.isArray(scope.zones) ? scope.zones[0] : ''))),
    readableLabel(anchorScope.area_label || scope.area_label),
  );
  if (namedZone && !isCameraLevelScope(namedZone) && !looksLikeUuid(namedZone)) return namedZone;
  const camera = formatCameraLevelScope({
    siteArea: text(anchorScope.zone_id || scope.zone_id || namedZone) || null,
    cameraLabel: customerScanCameraLabel(text(anchorScope.camera_label || scope.camera_label))
      || resolveConfiguredCameraLabel(text(anchorScope.camera_id || args.cameraId || scope.camera_id))
      || readableLabel(anchorScope.camera_label || scope.camera_label),
    cameraId: text(anchorScope.camera_id || args.cameraId || scope.camera_id) || null,
  });
  if (camera && camera !== CAMERA_LEVEL_SCOPE_LABEL) return camera;
  if (camera) return camera;
  const site = readableLabel(anchorScope.site_id || scope.site_id);
  return site || WHERE_UNAVAILABLE;
}

function canonicalNumerator(funnel: Record<string, unknown>, measurement: Record<string, unknown>): {
  count: number | null;
  source: 'confirmed_reviewed_observations' | 'confirmed_count' | null;
} {
  const reviewedObs = optionalNumber(
    measurement.confirmed_reviewed_observations
    ?? funnel.confirmed_reviewed_observations,
  );
  if (reviewedObs != null) {
    return { count: Math.trunc(reviewedObs), source: 'confirmed_reviewed_observations' };
  }
  const confirmed = optionalNumber(funnel.confirmed_count ?? measurement.confirmed_count);
  if (confirmed != null) {
    return { count: Math.trunc(confirmed), source: 'confirmed_count' };
  }
  return { count: null, source: null };
}

function usableHoursOf(measurement: Record<string, unknown>, report: unknown): number | null {
  const direct = optionalNumber(
    measurement.usable_hours
    ?? measurement.total_usable_hours
    ?? measurement.monitored_hours,
  );
  if (direct != null && direct > 0) return direct;
  const bodies = reportBodies(report);
  for (const body of bodies) {
    const hours = optionalNumber(body.usable_hours || nested(body, ['finding', 'usable_hours']));
    if (hours != null && hours > 0) return hours;
  }
  return null;
}

function ratesAllowed(measurement: Record<string, unknown>, coverageRefusesRate?: boolean): boolean {
  if (coverageRefusesRate) return false;
  if (measurement.rates_available === false) return false;
  if (measurement.rates_available === true) return true;
  return false;
}

function formatHours(hours: number): string {
  const rounded = hours >= 10 ? hours.toFixed(0) : hours.toFixed(1);
  return `${rounded.replace(/\.0$/, '')} usable hours`;
}

function recurrenceLabel(args: {
  report?: unknown;
  logicalScanCount: number;
  persisted?: string | null;
}): string {
  const stored = readableLabel(args.persisted);
  if (stored && /across three safety scans/i.test(stored) && args.logicalScanCount < 3) {
    return RECURRENCE_UNAVAILABLE;
  }
  if (stored) return stored;
  const bodies = reportBodies(args.report);
  for (const body of bodies) {
    const label = firstText(
      body.recurrence_headline,
      body.recurrence_label,
      body.recurrence_detail,
      nested(body, ['pattern', 'recurrence_label']),
      nested(body, ['risk_pattern_summary', 'recurrence_source']),
    );
    if (label) return label;
  }
  // Live Case linked_report_count / logicalScanCount is current longitudinal
  // evidence, not first-Scan-authoritative recurrence. Do not freeze later
  // Scan history onto the first-Scan finding.
  return RECURRENCE_UNAVAILABLE;
}

function operatingFinding(args: {
  report?: unknown;
  caseScope?: Record<string, unknown> | null;
  whereLabel: string;
  riskType?: string | null;
}): string {
  const workplace = readWorkplaceCondition(args.caseScope);
  const storedSummary = readableLabel(workplace.condition_summary);
  if (
    storedSummary
    && !hasCausalLanguage(storedSummary)
    && !/\brecurring\b/i.test(storedSummary)
  ) {
    return storedSummary;
  }
  const bodies = reportBodies(args.report);
  for (const body of bodies) {
    const finding = firstText(
      body.operating_condition,
      nested(body, ['safety_finding', 'statement']),
      nested(body, ['executive_summary', 'lede']),
    );
    if (finding && !hasCausalLanguage(finding)) return finding;
  }
  const condition = customerScanConditionLabel(
    text(workplace.condition_type || args.riskType || asRecord(args.caseScope).risk_type),
  );
  if (condition && !hasCausalLanguage(condition)) {
    return `${condition} observed in ${args.whereLabel}.`;
  }
  return FINDING_UNAVAILABLE;
}

export function presentFirstScanFinding(input: {
  hasEligibleScan: boolean;
  baselineWaiting?: boolean;
  reportId?: string | null;
  signed?: boolean;
  windowStart?: string | null;
  windowEnd?: string | null;
  timeZone?: string | null;
  report?: unknown;
  caseScope?: Record<string, unknown> | null;
  locationLabel?: string | null;
  cameraId?: string | null;
  progress?: Record<string, unknown> | null;
  riskType?: string | null;
  logicalScanCount?: number;
  persistedRecurrence?: string | null;
  coverageRefusesRate?: boolean;
  openScanHref?: string | null;
}): FirstScanFindingView {
  if (!input.hasEligibleScan) {
    return {
      empty: true,
      emptyLabel: input.baselineWaiting ? FIRST_SCAN_BASELINE_WAITING : FIRST_SCAN_EMPTY,
      signed: false,
      reportId: null,
      windowLabel: '',
      whereLabel: '',
      whenLabel: '',
      concentrationLabel: '',
      observedLabel: '',
      coverageLabel: '',
      rateLabel: null,
      recurrenceLabel: '',
      findingLabel: '',
      openScanHref: null,
      historical: false,
      logicalScanCount: 0,
    };
  }
  const windowLabel = formatHumanScanWindow(input.windowStart, input.windowEnd, input.timeZone);
  const concentration = timeConcentrationFrom(input.report);
  const proof = hotspotProof(input.report, concentration);
  const where = whereLabel({
    report: input.report,
    caseScope: input.caseScope,
    locationLabel: input.locationLabel,
    cameraId: input.cameraId,
    concentrationProof: proof,
  });
  const concentrationLabel = concentration.peakWhen || NO_CLEAR_TIME_CONCENTRATION;
  const funnel = funnelFrom(input.report, input.progress);
  const measurement = measurementFrom(input.report);
  const numerator = canonicalNumerator(funnel, measurement);
  const hours = usableHoursOf(measurement, input.report);
  const observedCountLabel = numerator.count == null
    ? null
    : `${numerator.count} confirmed reviewed observation${numerator.count === 1 ? '' : 's'}`;
  const observedLabel = observedCountLabel || OBSERVED_UNAVAILABLE;
  const coverageLabel = hours == null
    ? COVERAGE_UNAVAILABLE
    : (observedCountLabel
      ? `${observedCountLabel} across ${formatHours(hours)}`
      : `Measured across ${formatHours(hours)}`);
  let rateLabel: string | null = null;
  if (ratesAllowed(measurement, input.coverageRefusesRate) && hours != null && numerator.count != null) {
    const storedRate = optionalNumber(measurement.overall_rate ?? measurement.rate_per_usable_hour);
    if (storedRate != null) {
      rateLabel = `${storedRate} confirmed reviewed observations per usable hour across ${formatHours(hours)}`;
    }
  }
  return {
    empty: false,
    emptyLabel: null,
    signed: Boolean(input.signed),
    reportId: text(input.reportId) || null,
    windowLabel,
    whereLabel: where,
    whenLabel: windowLabel,
    concentrationLabel,
    observedLabel,
    coverageLabel,
    rateLabel,
    recurrenceLabel: recurrenceLabel({
      report: input.report,
      logicalScanCount: input.logicalScanCount || 1,
      persisted: input.persistedRecurrence,
    }),
    findingLabel: replaceInternalLocationTokens(operatingFinding({
      report: input.report,
      caseScope: input.caseScope,
      whereLabel: where,
      riskType: input.riskType,
    })),
    openScanHref: text(input.openScanHref) || null,
    historical: (input.logicalScanCount || 0) > 1,
    logicalScanCount: input.logicalScanCount || 1,
  };
}
